/**
 * Pixel tracking — Facebook, TikTok, GA4, GTM.
 *
 * Use the SAME pixel IDs as the Shopify storefront so behavior on
 * `app.storiz.ma` lands in the same Ads Manager / Events Manager / GA
 * accounts as `storiz.ma`. Configured per-environment in admin settings.
 *
 * Usage:
 *   initPixels({ facebookPixelId, tiktokPixelId, ga4MeasurementId })
 *   trackPageView('/editor/123/1/edit')
 *   trackEvent('cover_selected', { cover_id: 'xyz' })
 */

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
    ttq?: { load: (id: string, opts?: unknown) => void; page: () => void; track: (name: string, props?: unknown) => void; [k: string]: unknown };
    TiktokAnalyticsObject?: string;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[] };
  }
}

interface PixelConfig {
  facebookPixelId?: string | null;
  tiktokPixelId?: string | null;
  ga4MeasurementId?: string | null;
  gtmContainerId?: string | null;
  clarityProjectId?: string | null;
}

const initialized: PixelConfig = {};

/** Idempotent — safe to call multiple times. Only injects scripts on first init per ID. */
export function initPixels(cfg: PixelConfig): void {
  if (typeof window === 'undefined') return;

  // ── Facebook Pixel ────────────────────────────────────────────────────────
  if (cfg.facebookPixelId && initialized.facebookPixelId !== cfg.facebookPixelId) {
    if (!window.fbq) {
      // Standard Meta Pixel base code, lightly typed.
      /* eslint-disable @typescript-eslint/no-explicit-any */
      (function (f: any, b: Document, e: string, v: string) {
        if (f.fbq) return;
        const n: any = (f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        });
        if (!f._fbq) f._fbq = n;
        n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
        const t = b.createElement(e) as HTMLScriptElement;
        t.async = true; t.src = v;
        const s = b.getElementsByTagName(e)[0];
        s.parentNode!.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      /* eslint-enable @typescript-eslint/no-explicit-any */
    }
    // MANUAL ADVANCED MATCHING (2026-07-16): init with any customer data
    // already known (setAdvancedMatching stores it) — the pixel hashes it.
    //
    // MULTIPLE PIXELS (2026-08-01): facebookPixelId accepts a COMMA-SEPARATED
    // list so several Meta ad accounts can be fed at once, matching the
    // storefront theme setting. Only the init calls loop — fbq('track', …)
    // fans out to every initialised pixel by itself.
    const fbIds = splitPixelIds(cfg.facebookPixelId);
    for (const id of fbIds) window.fbq!('init', id, fbAdvancedMatching ?? {});
    window.fbq!('track', 'PageView');
    initialized.facebookPixelId = cfg.facebookPixelId;
    fbAmPixelIds = fbIds;
  }

  // ── TikTok Pixel ──────────────────────────────────────────────────────────
  if (cfg.tiktokPixelId && initialized.tiktokPixelId !== cfg.tiktokPixelId) {
    if (!window.ttq) {
      // Standard TikTok pixel loader.
      /* eslint-disable @typescript-eslint/no-explicit-any */
      (function (w: any, d: Document, t: string) {
        w.TiktokAnalyticsObject = t;
        const ttq = (w[t] = w[t] || []);
        ttq.methods = ['page','track','identify','instances','debug','on','off','once','ready','alias','group','enableCookie','disableCookie'];
        ttq.setAndDefer = function (target: any, method: string) {
          target[method] = function () { target.push([method].concat(Array.prototype.slice.call(arguments, 0))); };
        };
        for (const m of ttq.methods) ttq.setAndDefer(ttq, m);
        ttq.instance = function (id: string) {
          const e = ttq._i[id] || [];
          for (const m of ttq.methods) ttq.setAndDefer(e, m);
          return e;
        };
        ttq.load = function (id: string, opts?: unknown) {
          const url = 'https://analytics.tiktok.com/i18n/pixel/events.js';
          ttq._i = ttq._i || {}; ttq._i[id] = []; ttq._i[id]._u = url;
          ttq._t = ttq._t || {}; ttq._t[id] = +new Date();
          ttq._o = ttq._o || {}; ttq._o[id] = opts || {};
          const o = d.createElement('script') as HTMLScriptElement;
          o.type = 'text/javascript'; o.async = true;
          o.src = `${url}?sdkid=${id}&lib=${t}`;
          const a = d.getElementsByTagName('script')[0];
          a.parentNode!.insertBefore(o, a);
        };
      })(window, document, 'ttq');
      /* eslint-enable @typescript-eslint/no-explicit-any */
    }
    window.ttq!.load(cfg.tiktokPixelId);
    window.ttq!.page();
    initialized.tiktokPixelId = cfg.tiktokPixelId;
  }

  // ── Google Analytics 4 (gtag.js) ──────────────────────────────────────────
  if (cfg.ga4MeasurementId && initialized.ga4MeasurementId !== cfg.ga4MeasurementId) {
    if (!document.querySelector('script[data-storiz-pixel="ga4"]')) {
      const s = document.createElement('script');
      s.async = true;
      s.dataset.storizPixel = 'ga4';
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(cfg.ga4MeasurementId)}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () { window.dataLayer!.push(arguments); };
      window.gtag('js', new Date());
    }
    window.gtag!('config', cfg.ga4MeasurementId);
    initialized.ga4MeasurementId = cfg.ga4MeasurementId;
  }

  // ── Google Tag Manager (optional, alternative to direct GA4) ──────────────
  if (cfg.gtmContainerId && initialized.gtmContainerId !== cfg.gtmContainerId) {
    if (!document.querySelector('script[data-storiz-pixel="gtm"]')) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
      const s = document.createElement('script');
      s.async = true;
      s.dataset.storizPixel = 'gtm';
      s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(cfg.gtmContainerId)}`;
      document.head.appendChild(s);
    }
    initialized.gtmContainerId = cfg.gtmContainerId;
  }

  // ── Microsoft Clarity (session replays + heatmaps) ───────────────────────
  if (cfg.clarityProjectId && initialized.clarityProjectId !== cfg.clarityProjectId) {
    if (!document.querySelector('script[data-storiz-pixel="clarity"]')) {
      // Standard Clarity tag — queues calls to window.clarity until the
      // remote script loads, then drains the queue.
      /* eslint-disable @typescript-eslint/no-explicit-any */
      (function (c: any, l: Document, a: string, r: string, i: string) {
        c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
        const t = l.createElement(r) as HTMLScriptElement;
        t.async = true;
        t.src = `https://www.clarity.ms/tag/${i}`;
        (t as HTMLElement).dataset.storizPixel = 'clarity';
        const y = l.getElementsByTagName(r)[0];
        y.parentNode!.insertBefore(t, y);
      })(window, document, 'clarity', 'script', cfg.clarityProjectId);
      /* eslint-enable @typescript-eslint/no-explicit-any */
    }
    initialized.clarityProjectId = cfg.clarityProjectId;
  }
}

// ── Manual advanced matching (2026-07-16) ───────────────────────────────────
// Meta match-quality: pass the known customer's phone/name to the pixel (it
// SHA-256-hashes values itself). Called by the flow/editor once the order
// loads; re-inits the already-loaded pixel so subsequent events match.
let fbAdvancedMatching: Record<string, string> | null = null;
let fbAmPixelIds: string[] = [];

/** One id or a comma-separated list → clean array. Blank entries dropped so a
 *  stray comma can't produce an fbq('init', '') call. */
export function splitPixelIds(raw: string | null | undefined): string[] {
  return String(raw ?? '').split(',').map((s) => s.trim()).filter(Boolean);
}
export function setAdvancedMatching(data: { phone?: string | null; name?: string | null; email?: string | null }): void {
  const am: Record<string, string> = {};
  const phone = (data.phone ?? '').replace(/\D/g, '');
  if (phone) am.ph = phone.startsWith('0') ? `212${phone.slice(1)}` : phone;
  const name = (data.name ?? '').trim();
  if (name) {
    const parts = name.split(/\s+/);
    am.fn = parts[0].toLowerCase();
    am.ln = (parts.slice(1).join(' ') || parts[0]).toLowerCase();
  }
  if (data.email) am.em = data.email.trim().toLowerCase();
  if (Object.keys(am).length === 0) return;
  am.country = 'ma';
  fbAdvancedMatching = am;
  // Advanced matching has to be applied PER pixel — a later fbq('track') fans
  // out to all of them, but the matching payload does not.
  const pids = fbAmPixelIds.length ? fbAmPixelIds : splitPixelIds(initialized.facebookPixelId);
  if (typeof window !== 'undefined' && window.fbq) {
    for (const pid of pids) window.fbq('init', pid, am);
  }
}

/**
 * Staff pages never reach an ad platform (tracking v2, owner 2026-10-27): the
 * admin, the printer's page and the sign-in screens are the team's own
 * traffic, and counting it as visitors skews every audience Meta builds. Pure.
 */
const STAFF_PATH = /^\/(admin|imprimeur|login|signup|auth)(\/|$)/;
export function isStaffPath(path: string | null | undefined): boolean {
  return STAFF_PATH.test(String(path ?? ''));
}

/** Fire a PageView on all enabled pixels. Call on every SPA route change. */
export function trackPageView(path?: string): void {
  if (typeof window === 'undefined') return;
  if (window.fbq) window.fbq('track', 'PageView');
  if (window.ttq?.page) window.ttq.page();
  if (window.gtag) window.gtag('event', 'page_view', { page_path: path ?? window.location.pathname });
}

/** Fire a custom event on all enabled pixels. Use snake_case event names. */
export function trackEvent(name: string, props?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  if (window.fbq) window.fbq('trackCustom', name, props ?? {});
  if (window.ttq?.track) window.ttq.track(name, props);
  if (window.gtag) window.gtag('event', name, props);
  // Clarity uses 'event' for custom events; props are attached as tags.
  if (window.clarity) {
    window.clarity('event', name);
    if (props) {
      for (const [k, v] of Object.entries(props)) {
        try { window.clarity('set', k, String(v)); } catch { /* ignore */ }
      }
    }
  }
  // Console echo so the admin can see events in DevTools without needing
  // browser extensions.
  console.log('[pixels]', name, props ?? {});
}

/** Status for the admin "Test" buttons — which pixels are live in the page. */
export function pixelStatus(): { fb: boolean; tiktok: boolean; ga4: boolean; gtm: boolean; clarity: boolean } {
  if (typeof window === 'undefined') return { fb: false, tiktok: false, ga4: false, gtm: false, clarity: false };
  return {
    fb: !!window.fbq,
    tiktok: !!window.ttq,
    ga4: !!window.gtag && !!initialized.ga4MeasurementId,
    gtm: !!initialized.gtmContainerId,
    clarity: !!window.clarity,
  };
}
